import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageSquare } from "lucide-react";
import { IMAGES, SITE } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { restaurantSchema, breadcrumbSchema } from "@/lib/schema";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OrderButton, CallLink, TextLink, DirectionsLink } from "@/components/TrackedLinks";
import { SignatureFavorites } from "@/components/SignatureFavorites";
import { ReviewsSection } from "@/components/Reviews";
import { CateringPackages } from "@/pages/CateringPage";
import { CateringQuoteForm } from "@/components/CateringQuoteForm";

const LandingHero = ({ eyebrow, title, copy, image, alt, children, testId }) => (
  <section className="py-16 px-4" data-testid={testId}>
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{eyebrow}</p>
        <h1 className="font-serif text-5xl md:text-6xl text-hacienda-charcoal font-semibold mb-6 leading-[1.05]">{title}</h1>
        <p className="text-hacienda-gray text-lg leading-relaxed mb-8">{copy}</p>
        <div className="flex flex-col sm:flex-row gap-4">{children}</div>
      </div>
      <img src={image} alt={alt} width="800" height="1066" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3] lg:aspect-[4/5]" />
    </div>
  </section>
);

const LocalInfo = () => {
  const { t } = useLang();
  return (
    <section className="py-16 px-4 bg-white border-y border-hacienda-border-light" data-testid="landing-local-info">
      <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-8 text-center">
        <div><p className="text-xs uppercase tracking-wider text-hacienda-gray mb-1">{t("visit.address")}</p><address className="not-italic font-medium text-hacienda-charcoal">{SITE.address.street}<br />{SITE.address.city}, {SITE.address.state} {SITE.address.zip}</address></div>
        <div><p className="text-xs uppercase tracking-wider text-hacienda-gray mb-1">{t("visit.hours")}</p><p className="font-medium text-hacienda-charcoal">{t("days.tueThu")}: 11 AM – 9 PM<br />{t("days.friSat")}: 11 AM – 10 PM<br />{t("days.sunMon")}: {t("days.closed")}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-hacienda-gray mb-1">{t("visit.phone")}</p><a href={`tel:${SITE.phoneE164}`} className="font-medium text-hacienda-charcoal hover:text-hacienda-terracotta">{SITE.phoneDisplay}</a><div className="mt-3"><DirectionsLink location="landing" size="sm">{t("actions.directions")}</DirectionsLink></div></div>
      </div>
    </section>
  );
};

export const BirriaTacosLanding = () => {
  const { t } = useLang();
  const crumb = [{ label: "Birria Tacos Albuquerque", to: "/birria-tacos-albuquerque" }];
  return (
    <div data-testid="landing-tacos-page">
      <Seo
        title="Birria Tacos in Albuquerque | Quesabirria Near Osuna Rd | Tijuas Tacos"
        description="Looking for birria tacos in Albuquerque? Tijuas Tacos Birrieria serves Tijuana-style quesabirria tacos, tacos dorados and consomé at 5505 Osuna Rd NE. Order online for pickup or delivery."
        path="/birria-tacos-albuquerque"
        jsonLd={[restaurantSchema(), breadcrumbSchema(crumb)]}
      />
      <Breadcrumbs items={crumb} />
      <LandingHero
        testId="landing-tacos-hero"
        eyebrow="Albuquerque · Osuna Rd NE"
        title="Birria Tacos in Albuquerque"
        copy="Tijuas Tacos Birrieria is a locally owned birrieria in Northeast Albuquerque specializing in Tijuana-style birria de res. Our quesabirria tacos are griddled with cheese until crispy and served with a rich consomé for dipping—plus tacos dorados, birria ramen, birria fries and our shareable birria pizza. Order online for pickup or delivery, or stop in on Osuna Rd NE near I-25."
        image={IMAGES.quesabirria}
        alt="Quesabirria tacos with consomé at Tijuas Tacos Birrieria in Albuquerque"
      >
        <OrderButton location="landing_tacos" size="lg" data-testid="landing-order-btn" />
        <Link to="/menu" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-hacienda-terracotta text-hacienda-terracotta hover:bg-hacienda-terracotta hover:text-white px-9 py-4 text-lg font-medium transition-colors">{t("actions.viewMenu")} <ArrowRight size={18} aria-hidden="true" /></Link>
      </LandingHero>
      <LocalInfo />
      <SignatureFavorites />
      <section className="py-20 px-4" data-testid="landing-tacos-faq">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="font-serif text-4xl text-hacienda-charcoal font-semibold text-center mb-10">Birria FAQ</h2>
          {[
            ["What is quesabirria?", "Quesabirria is a taco made with a corn tortilla dipped in birria consomé, filled with melted cheese and slow-cooked beef birria, then griddled until crispy. It's served with a cup of consomé for dipping."],
            ["What kind of birria do you serve?", "We serve birria de res (beef) prepared Tijuana-style—the same style that made quesabirria tacos famous."],
            ["Do you offer takeout and delivery?", "Yes. Order online for pickup at 5505 Osuna Rd NE or choose delivery through our ordering partner."],
            ["Do you cater?", "Yes—taco boxes, birria by the pound and birria pizza packages for events of all sizes. See our catering page to request a quote."],
          ].map(([q, a]) => (
            <div key={q} className="bg-hacienda-cream p-6 rounded-2xl">
              <h3 className="font-semibold text-hacienda-charcoal text-lg mb-2">{q}</h3>
              <p className="text-hacienda-gray">{a}</p>
            </div>
          ))}
        </div>
      </section>
      <ReviewsSection className="bg-hacienda-cream" />
    </div>
  );
};

export const BirriaCateringLanding = () => {
  const { t } = useLang();
  const crumb = [{ label: "Birria Catering Albuquerque", to: "/birria-catering-albuquerque" }];
  return (
    <div data-testid="landing-catering-page">
      <Seo
        title="Birria Catering in Albuquerque | Taco Boxes & Birria by the Pound | Tijuas Tacos"
        description="Birria catering for Albuquerque weddings, quinceañeras, graduations, birthdays and office lunches. Quesabirria taco boxes, tacos dorados, birria by the pound and birria pizza packages from Tijuas Tacos Birrieria. Get a quote."
        path="/birria-catering-albuquerque"
        jsonLd={[restaurantSchema(), breadcrumbSchema(crumb)]}
      />
      <Breadcrumbs items={crumb} />
      <LandingHero
        testId="landing-catering-hero"
        eyebrow="Albuquerque Catering"
        title="Birria Catering in Albuquerque"
        copy="Feeding a crowd? Tijuas Tacos Birrieria caters Tijuana-style birria de res across Albuquerque—quesabirria taco boxes from 50 to 200 tacos, tacos dorados, birria by the pound with tortillas and consomé, and 14-inch birria pizza packages. Every package includes limes, onion, cilantro and red and green salsas. Pickup or delivery, with rice, beans and aguas frescas available as add-ons."
        image={IMAGES.birriaPizza}
        alt="Birria pizza prepared for catering by Tijuas Tacos Birrieria"
      >
        <a href="#quote-form" className="inline-flex items-center justify-center rounded-full bg-hacienda-terracotta hover:bg-hacienda-terracotta-dark text-white px-9 py-4 text-lg font-medium transition-colors" data-testid="landing-quote-anchor">{t("actions.quote")}</a>
        <CallLink location="landing_catering" size="lg"><Phone size={20} aria-hidden="true" /> {t("actions.call")}</CallLink>
        <TextLink location="landing_catering" size="lg"><MessageSquare size={20} aria-hidden="true" /> {t("actions.text")}</TextLink>
      </LandingHero>
      <LocalInfo />
      <section className="py-20 px-4" data-testid="landing-catering-packages">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-hacienda-charcoal font-semibold mb-10 text-center">{t("catering.packages")}</h2>
          <CateringPackages />
        </div>
      </section>
      <section id="quote-form" className="py-20 px-4 bg-hacienda-cream scroll-mt-24">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl text-hacienda-charcoal font-semibold mb-10 text-center">{t("catering.formTitle")}</h2>
          <CateringQuoteForm />
        </div>
      </section>
    </div>
  );
};
